import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaCheck, FaChevronDown, FaArrowRight,
    FaGlobe, FaReact, FaDatabase, FaCloud,
    FaWhatsapp, FaPhone,
    FaBuilding, FaGraduationCap, FaClinicMedical,
    FaShoppingCart, FaUtensils, FaDumbbell, FaPray,
    FaBookOpen, FaHome, FaEllipsisH,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useSEO, { SITE_URL } from '../utils/useSEO';
import './Pricing.css';

const PRICING_SCHEMA = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "WebPage",
            "@id": `${SITE_URL}/pricing#webpage`,
            "url": `${SITE_URL}/pricing`,
            "name": "Pricing | Website Subscription Plans | Nimai Solutions",
            "description": "Simple monthly subscription plans for professional websites. Starting at ₹799/month. No large upfront cost.",
            "isPartOf": { "@id": `${SITE_URL}/#website` },
        }
    ]
};

const PLANS = [
    {
        key: 'in',
        domain: '.in Domain',
        emoji: '🇮🇳',
        price: 799,
        tagline: 'Perfect for Indian businesses',
        color: '#10b981',
        gradient: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
        popular: false,
    },
    {
        key: 'com',
        domain: '.com Domain',
        emoji: '🌐',
        price: 999,
        tagline: 'Best for global presence',
        color: '#4f46e5',
        gradient: 'linear-gradient(135deg, #ede9fe 0%, #c4b5fd 100%)',
        popular: true,
    },
    {
        key: 'custom',
        domain: 'Your Own Domain',
        emoji: '⚙️',
        price: 899,
        tagline: 'Use your existing domain',
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        popular: false,
    },
];

const FEATURES = [
    'Fully Custom Website Based on Your Requirements',
    'Custom Admin Panel Included',
    'Domain Included',
    'Hosting Included',
    'SSL Security Included',
    'Mobile Responsive Design',
    'WhatsApp Integration',
    'Contact & Inquiry Forms',
    'SEO-Friendly Structure',
    'Fast Loading Website',
    'Regular Maintenance',
    'Lifetime Free Technical Support',
    'Bug Fixes & Security Updates',
];

const TECH_STACK = [
    { icon: <FaReact />,    label: 'Frontend',       value: 'React.js',                color: '#61dafb' },
    { icon: <FaGlobe />,    label: 'Backend',        value: 'Node.js / Laravel',        color: '#68a063' },
    { icon: <FaDatabase />, label: 'Database',       value: 'MySQL / PostgreSQL',       color: '#336791' },
    { icon: <FaCloud />,    label: 'Cloud Hosting',  value: 'Included',                 color: '#f59e0b' },
    { icon: <FaBuilding />, label: 'Admin Panel',    value: 'Custom Dashboard',         color: '#8b5cf6' },
];

const BUSINESS_TYPES = [
    { icon: <FaBuilding />,      label: 'Business' },
    { icon: <FaGraduationCap />, label: 'School / Academy' },
    { icon: <FaClinicMedical />, label: 'Clinic / Hospital' },
    { icon: <FaShoppingCart />,  label: 'Store' },
    { icon: <FaUtensils />,      label: 'Restaurant' },
    { icon: <FaDumbbell />,      label: 'Gym' },
    { icon: <FaPray />,          label: 'Temple / NGO' },
    { icon: <FaBookOpen />,      label: 'Coaching Class' },
    { icon: <FaHome />,          label: 'Real Estate' },
    { icon: <FaEllipsisH />,     label: 'Any Business' },
];

const FAQS = [
    {
        q: 'Is there any setup / one-time fee?',
        a: 'No. There is no large upfront development cost. You only pay the monthly subscription amount. We handle everything — development, hosting, maintenance, and support.',
    },
    {
        q: 'What happens if I stop the subscription?',
        a: 'You can cancel anytime. If you cancel, the website will be taken offline. We recommend continuing the subscription to keep your website live and maintained.',
    },
    {
        q: 'Is the domain and hosting really included?',
        a: 'Yes — the domain and hosting are fully included in your monthly plan. You don\'t need to purchase anything separately.',
    },
    {
        q: 'How long does it take to build my website?',
        a: 'Most websites are delivered within 3–7 days after we receive your requirements and content. Complex custom projects may take a bit longer.',
    },
    {
        q: 'Can I upgrade or change my plan later?',
        a: 'Absolutely. You can switch plans or add features anytime. Our team will handle the migration seamlessly.',
    },
    {
        q: 'Do you work with clients outside Vadodara / Gujarat?',
        a: 'Yes — we work with clients across India. All communication, development, and delivery happens remotely with no compromise on quality.',
    },
];

/* ─── FAQ ITEM ─── */
const FAQItem = ({ faq, index }) => {
    const [open, setOpen] = useState(false);
    return (
        <motion.div
            className={`faq-item ${open ? 'open' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.07 }}
        >
            <button className="faq-question" onClick={() => setOpen(!open)}>
                <span>{faq.q}</span>
                <FaChevronDown className={`faq-arrow ${open ? 'rotated' : ''}`} />
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        className="faq-answer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: 'easeInOut' }}
                    >
                        <p>{faq.a}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

/* ─── MAIN ─── */
const Pricing = () => {
    const navigate = useNavigate();

    useSEO({
        title:       "Pricing | Website Subscription Plans | Nimai Solutions",
        description: "Professional websites on a simple monthly subscription. Starting ₹799/month. Domain, hosting, SSL, admin panel all included. No large upfront cost.",
        canonical:   `${SITE_URL}/pricing`,
        keywords:    "website subscription India, monthly website plan, affordable website Vadodara, website maintenance plan",
        schema:      PRICING_SCHEMA,
    });

    return (
        <div className="pricing-container">
            <Navbar />

            {/* ── HERO ── */}
            <section className="pricing-hero">
                <div className="pricing-hero-grid" />
                <motion.div
                    className="pricing-hero-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="pricing-badge">Monthly Subscription Model</span>
                    <h1>Professional Website.<br /><span>No Large Upfront Cost.</span></h1>
                    <p>
                        Hi 👋 — We provide fully custom websites on a simple monthly subscription.
                        Just pay monthly while we handle development, hosting, maintenance, security &amp; support.
                    </p>
                    <div className="hero-actions">
                        <button className="hero-cta-primary" onClick={() => navigate('/client-onboarding')}>
                            Get Started <FaArrowRight />
                        </button>
                        <a href="https://wa.me/917046548990" target="_blank" rel="noreferrer" className="hero-cta-wa">
                            <FaWhatsapp /> Chat on WhatsApp
                        </a>
                    </div>
                </motion.div>
            </section>

            {/* ── PLANS ── */}
            <section className="plans-section">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="section-badge">💻 Website Subscription Plans</span>
                    <h2>Simple, Transparent <span>Pricing</span></h2>
                    <p>Choose the plan that fits your domain preference. Everything else is included.</p>
                </motion.div>

                <div className="plans-grid">
                    {PLANS.map((plan, i) => (
                        <motion.div
                            key={plan.key}
                            className={`plan-card ${plan.popular ? 'plan-card--popular' : ''}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            style={{ '--plan-color': plan.color }}
                        >
                            {plan.popular && <div className="plan-popular-badge">⭐ Most Popular</div>}

                            <div className="plan-icon-wrap" style={{ background: plan.gradient }}>
                                <span className="plan-emoji">{plan.emoji}</span>
                            </div>

                            <h3 className="plan-domain">{plan.domain}</h3>
                            <p className="plan-tagline">{plan.tagline}</p>

                            <div className="plan-price-wrap">
                                <span className="plan-currency">₹</span>
                                <span className="plan-price">{plan.price.toLocaleString('en-IN')}</span>
                                <span className="plan-period">/month</span>
                            </div>

                            <ul className="plan-features">
                                {FEATURES.map((f, fi) => (
                                    <li key={fi}>
                                        <span className="plan-check" style={{ color: plan.color }}><FaCheck /></span>
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            <button
                                className="plan-cta"
                                style={{ background: plan.popular ? plan.color : 'transparent', borderColor: plan.color, color: plan.popular ? '#fff' : plan.color }}
                                onClick={() => navigate('/client-onboarding')}
                            >
                                Get Started <FaArrowRight />
                            </button>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── TECH STACK ── */}
            <section className="tech-section">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="section-badge">⚙️ Technology Stack</span>
                    <h2>Built with <span>Modern Technology</span></h2>
                </motion.div>

                <div className="tech-grid">
                    {TECH_STACK.map((t, i) => (
                        <motion.div
                            key={i}
                            className="tech-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.08 }}
                        >
                            <span className="tech-icon" style={{ color: t.color, background: t.color + '18' }}>
                                {t.icon}
                            </span>
                            <div>
                                <p className="tech-label">{t.label}</p>
                                <p className="tech-value">{t.value}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── WHO WE SERVE ── */}
            <section className="who-section">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="section-badge">🏢 Who We Serve</span>
                    <h2>We Build for <span>Every Business</span></h2>
                    <p>Whether you need a website for a business, school, clinic, store or any organization — we can build a custom solution tailored to your needs.</p>
                </motion.div>

                <div className="who-grid">
                    {BUSINESS_TYPES.map((b, i) => (
                        <motion.div
                            key={i}
                            className="who-card"
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.06 }}
                        >
                            <span className="who-icon">{b.icon}</span>
                            <span className="who-label">{b.label}</span>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── WHY SUBSCRIPTION ── */}
            <section className="why-section">
                <motion.div
                    className="why-card"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <div className="why-left">
                        <span className="section-badge">🚀 Why Subscription?</span>
                        <h2>No Large Upfront<br /><span>Investment Required</span></h2>
                        <p>
                            Traditional web development costs ₹30,000–₹1,00,000+ upfront.
                            With our subscription model, you get a fully custom website with zero large payment.
                            Just a simple monthly fee while we handle everything.
                        </p>
                        <ul className="why-list">
                            <li><FaCheck /> Development included</li>
                            <li><FaCheck /> Hosting &amp; domain included</li>
                            <li><FaCheck /> Maintenance &amp; updates included</li>
                            <li><FaCheck /> Security &amp; SSL included</li>
                            <li><FaCheck /> Technical support included</li>
                            <li><FaCheck /> Cancel anytime</li>
                        </ul>
                        <button className="why-cta" onClick={() => navigate('/client-onboarding')}>
                            Talk to Us <FaArrowRight />
                        </button>
                    </div>
                    <div className="why-right">
                        <div className="why-contact-card">
                            <p className="why-contact-title">📞 Ready to Get Started?</p>
                            <p className="why-contact-sub">Share your requirements and our team will suggest the best solution for your business.</p>
                            <a href="tel:+917046548990" className="why-phone">
                                <FaPhone /> +91 70465 48990
                            </a>
                            <a href="https://wa.me/917046548990" target="_blank" rel="noreferrer" className="why-wa">
                                <FaWhatsapp /> Chat on WhatsApp
                            </a>
                            <div className="why-services">
                                <span>🌐 Website Development</span>
                                <span>📱 Mobile Apps</span>
                                <span>🏢 CRM Solutions</span>
                                <span>💻 Custom Software</span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* ── FAQ ── */}
            <section className="pricing-faq-section">
                <motion.div
                    className="faq-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <span className="section-badge">Got Questions?</span>
                    <h2>Frequently Asked <span>Questions</span></h2>
                </motion.div>
                <div className="faq-list">
                    {FAQS.map((faq, i) => (
                        <FAQItem key={i} faq={faq} index={i} />
                    ))}
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="pricing-cta-section">
                <div className="pricing-cta-card">
                    <div className="cta-blob blob-1" />
                    <div className="cta-blob blob-2" />
                    <motion.div
                        className="pricing-cta-inner"
                        initial={{ opacity: 0, scale: 0.96 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                    >
                        <span className="cta-tag">Free Consultation</span>
                        <h2>Ready to get your <span>website live?</span></h2>
                        <p>Share your requirements and we'll build a custom solution tailored to your business — no large upfront payment needed.</p>
                        <div className="cta-actions">
                            <button className="cta-primary" onClick={() => navigate('/client-onboarding')}>
                                Get Started Today
                            </button>
                            <a href="https://wa.me/917046548990" target="_blank" rel="noreferrer" className="cta-secondary">
                                <FaWhatsapp /> WhatsApp Us
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Pricing;
